import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { useFieldArray, useWatch } from 'react-hook-form';
import { t } from '../../../services/translationService';
import { InformationIcon } from '../../shared/InformationIcon';


interface Props {
  errors: any;
  register: any;
  control: any;
  getValues: any;
  setValue: any;
  trigger: any;
  blockRef: any;
  pageVariant: string;
  errorRows?: number[];
}

export const Eudr = ({
  errors,
  register,
  control,
  getValues,
  trigger,
  blockRef,
  pageVariant,
  errorRows,
}: Props) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(
    undefined
  );

  const {
    fields: eudrDokument,
    append: appendEudrDokument,
    remove: removeEudrDokument,
    update: updateEudrDokument
  } = useFieldArray({
    control,
    name: 'eudrDokument',
  });

  const eudrLagerflaggaWatch = useWatch({ control, name: 'eudrLagerflagga' });
  const eudrDocumentsWatch = useWatch({ control, name: 'eudrDokument' });

  const [eudrLagerflaggaVisible, setEudrLagerflaggaVisible] = useState(!eudrDokument?.length);
  const [eudrDokumentEditable, setEudrDokumentEditable] = useState(!!getValues('eudrLagerflagga'));

  useEffect(() => {
    setEudrDokumentEditable(!eudrLagerflaggaWatch);
  }, [eudrLagerflaggaWatch])

  useEffect(() => {
    setEudrLagerflaggaVisible(!eudrDocumentsWatch?.length);
  }, [eudrDocumentsWatch])

  const saveEudrDokument = () =>
    trigger(
      `eudrDokument.${currentIndex !== undefined ? currentIndex : eudrDokument.length - 1
      }`
    ).then((validResult: boolean) => {
      if (validResult) {
        const last = getValues(`eudrDokument.${eudrDokument.length - 1}`);

        currentIndex === undefined && removeEudrDokument(eudrDokument.length - 1);
        currentIndex !== undefined
          ? updateEudrDokument(
            currentIndex,
            getValues(`eudrDokument.${currentIndex}`)
          )
          : appendEudrDokument({
            ...last,
          });
        setShowModal(false);
      }
    });

  const displayModal = (index?: number) => {
    index === undefined ? appendEudrDokument({}) : setCurrentIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    currentIndex === undefined && removeEudrDokument(eudrDokument.length - 1);
  };

  return (
    <>
      <h3 ref={blockRef}>{t("eudr")}</h3>
      <Col xs={12}>
        <hr className="u-text-grey" />
      </Col>
      <Card className="my-4 nopadding">
        <Card.Body>
          {eudrDokument?.length > 0 && (
            <div className='mb-3 w-100 table'>
              <div className="small px-3 py-1 d-flex border-bottom">
                <div style={{ width: "20%" }}>
                  <span className="d-block">{t('eudrDokument')}</span>
                </div>
              </div>
              <div>
                {eudrDokument.map((row: any, index: number) => {
                  return (
                    <div
                      key={row.id}
                      className={`${index % 2 === 1 ? 'u-background-grey' : ''
                        } d-flex gx-0 px-3 u-text-small-medium u-text-grey py-3 align-items-center border-bottom`}
                    >
                      <div style={{ width: "90%" }}>
                        {getValues(`eudrDokument.${index}.referens`)}
                      </div>
                      <div style={{ width: "10%" }}>
                        <div className="d-flex gap-3 justify-content-end">
                          <i
                            className="bi bi-pencil u-text-primary u-cursor-pointer"
                            onClick={() => displayModal(index)}
                          />
                          <i
                            className="bi bi-trash3 u-text-error u-cursor-pointer"
                            onClick={() => removeEudrDokument(index)}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card.Body>
        <Card.Footer>
          <Button
            type="button"
            className="primary"
            disabled={!eudrDokumentEditable}
            onClick={() => displayModal()}
          >
            <i className="bi bi-plus" />
            {t('laggTill')}
          </Button>
        </Card.Footer>
      </Card>
      {eudrLagerflaggaVisible &&
        <>
        <Form.Group as={Col} className="mt-3 mb-3 col ms-3">
          <Form.Check
            label={<>
              {"Lagerflagga"}
              <InformationIcon
                text={t('hjalpLagerflagga')}
                inline={true}
                className="ps-1"
              />
            </>}
            {...register('eudrLagerflagga')}
          />
        </Form.Group>
      <Col xs={12}>
        <hr className="u-text-grey" />
        </Col>
      </>
     }

      <Modal
        show={showModal}
        centered
        size="lg"
        onHide={closeModal}
        onExited={() => setCurrentIndex(undefined)}
      >
        <Modal.Header>
          <p>{t("laggTill")}</p>
        </Modal.Header>
        <Modal.Body>
          {eudrDokument.map((row: any, index: number) => {
            return (
              index ===
              (currentIndex !== undefined
                ? currentIndex
                : eudrDokument.length - 1) && (
                <Row key={index}>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="mb-1 text-body">
                        {t("eudrReferens")}*
                        <InformationIcon
                          text={t('hjalpReferens')}
                          inline={true}
                          className="ps-1"
                          wide={true}
                        />
                      </Form.Label>
                      <Form.Control
                        key={row.id}
                        placeholder={t('skrivEllips')}
                        {...register(`eudrDokument.${index}.referens`,
                          { required: t("valideringEudrReferens") })
                        }
                        isInvalid={
                          !!errors.eudrDokument && errors.eudrDokument[index]?.referens
                        }
                      />
                      {!!errors.eudrDokument &&
                        errors.eudrDokument[index]?.referens && (
                          <Form.Control.Feedback type="invalid">
                            {`${errors.eudrDokument[index].referens.message}`}
                          </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    {/* Keep these so all fields are sent on save */}
                    <input
                      type="hidden"
                      {...register(`eudrDokument.${index}.typ`)}
                      defaultValue={row.typ ?? ''}
                    />

                    <input
                      type="hidden"
                      {...register(`eudrDokument.${index}.ursprungsland`)}
                      defaultValue={row.ursprungsland ?? ''}
                    />

                    <input
                      type="hidden"
                      {...register(`eudrDokument.${index}.avverkningsdatum`)}
                      defaultValue={row.avverkningsdatum ?? ''}
                    />

                    <input
                      type="hidden"
                      {...register(`eudrDokument.${index}.gpskoordinater`)}
                      defaultValue={row.gpskoordinater ?? ''}
                    />

                  </Col>
                </Row>
              )
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {t('avbryt')}
          </Button>
          <Button variant="primary" onClick={saveEudrDokument}>
            {t('spara')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
